import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Tabs, Tab, Modal, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllCourses,
  openCreateCourseDialog,
  openCreateNewCourseDialog,
  selectedCourse,
  deleteCourse,
} from '../Redux/Dashboard/DashboardSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faFilm,
  faFilePdf,
  faImage,
  faPen,
  faPlus,
  faPencil,
  faTrash,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import '../Style/AllCoursesShowing.css';
import judicialLogo from '../Images/judicialLogo.png';
import { Notify } from '../CommonPopup/DashboardPopUp';

const AllCoursesShowing = () => {
  const dispatch = useDispatch();
  const [currentScreen, setCurrentScreen] = useState('courses');
  const [selectedCourseCard, setSelectedCourseCard] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedContentTab, setSelectedContentTab] = useState('video');
  const [activeTab, setActiveTab] = useState('all');
  const [showDeleteCourseDialog, setShowDeleteCourseDialog] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [selectedTestGroup, setSelectedTestGroup] = useState(null);

  const [videoTitles, setVideoTitles] = useState(() => {
    return JSON.parse(localStorage.getItem('videoTitles') || '{}');
  });

  const coursesData = useSelector((state) => state.DashboardSlice.coursesData);

  useEffect(() => {
    dispatch(getAllCourses({ is_paid: '' }));
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentScreen]);

  const handleAddFiles = (course) => {
    dispatch(openCreateCourseDialog(true));
    dispatch(selectedCourse({ course }));
  };

  const handleCourseDelete = async () => {
    if (selectedCourseId) {
      try {
        await dispatch(deleteCourse(selectedCourseId)).unwrap();
        if (typeof Notify === 'function') {
          Notify({ data: 'success', msg: 'Course Deleted successfully', type: 'success' });
        } else {
          alert('Course Deleted successfully');
        }
        dispatch(getAllCourses({ is_paid: '' }));
        setShowDeleteCourseDialog(false);
        setSelectedCourseId(null);
      } catch (error) {
        console.error('Failed to delete course:', error);
        if (typeof Notify === 'function') {
          Notify({ data: 'error', msg: 'Failed to delete course', type: 'error' });
        } else {
          alert('Failed to delete course');
        }
      }
    }
  };

  const BackButton = ({ title, onClick }) => (
    <button type="button" className="back-button" onClick={onClick}>
      <FontAwesomeIcon icon={faArrowLeft} />
      {title}
    </button>
  );

  const CourseCard = ({ course }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const descriptionRef = useRef(null);

    useEffect(() => {
      const checkTruncation = () => {
        if (descriptionRef.current) {
          const { offsetHeight, scrollHeight } = descriptionRef.current;
          setIsTruncated(scrollHeight > offsetHeight);
        }
      };
      checkTruncation();
      window.addEventListener('resize', checkTruncation);
      return () => window.removeEventListener('resize', checkTruncation);
    }, [course.description]);

    const toggleExpand = (e) => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    };

    const handleDeleteClick = (e) => {
      e.stopPropagation();
      setSelectedCourseId(course._id);
      setShowDeleteCourseDialog(true);
    };

    return (
      <div
        className="card course-card"
        onClick={() => {
          setSelectedCourseCard(course);
          setSelectedLesson(null);
          setSelectedSubject(null);
          setCurrentScreen('lessons');
        }}
      >
        <div className={`paid-banner ${course.is_paid ? 'paid' : 'free'}`}>
          {course.is_paid ? 'Paid' : 'Free'}
        </div>
        <img
          src={course.thumbnail || 'https://via.placeholder.com/300x200?text=No+Image'}
          alt={course.title}
          className="card-img"
        />
        <div className="card-content">
          <div className="card-header-actions">
            <h5 className="card-title">{course.title || 'Untitled Course'}</h5>
            <button
              type="button"
              className="delete-course-btn"
              onClick={handleDeleteClick}
              aria-label={`Delete course ${course.title}`}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
          <div className="description-wrapper">
            <p
              className={`card-description ${isExpanded ? 'expanded' : ''}`}
              ref={descriptionRef}
            >
              {course.description || 'No description available.'}
            </p>
            {isTruncated && (
              <button
                type="button"
                className="more-link"
                onClick={toggleExpand}
                aria-label={`Show ${isExpanded ? 'less' : 'more'} description for ${course.title}`}
              >
                {isExpanded ? 'Less' : 'More'}
              </button>
            )}
          </div>
          <p className="card-meta">
            {course.lessons?.length || 0} Lesson{course.lessons?.length !== 1 ? 's' : ''}
          </p>
          <button
            type="button"
            className="add-files-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleAddFiles({ course });
            }}
            aria-label={`Edit course ${course.title}`}
          >
            <FontAwesomeIcon icon={faPencil} />
            Edit Course
          </button>
        </div>
      </div>
    );
  };

  const LessonCard = ({ lesson }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const descriptionRef = useRef(null);

    useEffect(() => {
      const checkTruncation = () => {
        if (descriptionRef.current) {
          const { offsetHeight, scrollHeight } = descriptionRef.current;
          setIsTruncated(scrollHeight > offsetHeight);
        }
      };
      checkTruncation();
      window.addEventListener('resize', checkTruncation);
      return () => window.removeEventListener('resize', checkTruncation);
    }, [lesson.description]);

    const toggleExpand = (e) => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    };

    return (
      <div
        className="card lesson-card"
        onClick={() => {
          setSelectedLesson(lesson);
          setSelectedSubject(null);
          setCurrentScreen('subjects');
        }}
      >
        <img
          src={lesson.thumbnail || 'https://via.placeholder.com/300x200?text=No+Image'}
          alt={lesson.lesson_name}
          className="card-img"
        />
        <div className="card-content">
          <h5 className="card-title">{lesson.lesson_name || 'Untitled Lesson'}</h5>
          <div className="description-wrapper">
            <p
              className={`card-description ${isExpanded ? 'expanded' : ''}`}
              ref={descriptionRef}
            >
              {lesson.description || 'No description available.'}
            </p>
            {isTruncated && (
              <button
                type="button"
                className="more-link"
                onClick={toggleExpand}
                aria-label={`Show ${isExpanded ? 'less' : 'more'} description for ${lesson.lesson_name}`}
              >
                {isExpanded ? 'Less' : 'More'}
              </button>
            )}
          </div>
          <p className="card-meta">
            {lesson.subjects?.length || 0} Subject{lesson.subjects?.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    );
  };

  const SubjectCard = ({ subject }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const descriptionRef = useRef(null);

    useEffect(() => {
      const checkTruncation = () => {
        if (descriptionRef.current) {
          const { offsetHeight, scrollHeight } = descriptionRef.current;
          setIsTruncated(scrollHeight > offsetHeight);
        }
      };
      checkTruncation();
      window.addEventListener('resize', checkTruncation);
      return () => window.removeEventListener('resize', checkTruncation);
    }, [subject.description]);

    const toggleExpand = (e) => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    };

    const videoCount = subject.content?.video?.length || 0;
    const pdfCount = subject.content?.pdf?.length || 0;
    const imageCount = subject.content?.image?.length || 0;
    const testCount = subject.content?.test?.length || 0;

    return (
      <div
        className="card subject-card"
        onClick={() => {
          setSelectedSubject(subject);
          setCurrentScreen('subjectDetails');
        }}
      >
        <img
          src={subject.thumbnail || 'https://via.placeholder.com/300x200?text=No+Image'}
          alt={subject.chapter_name}
          className="card-img"
        />
        <div className="card-content">
          <h5 className="card-title">{subject.chapter_name || 'Untitled Subject'}</h5>
          <div className="description-wrapper">
            <p
              className={`card-description ${isExpanded ? 'expanded' : ''}`}
              ref={descriptionRef}
            >
              {subject.description || 'No description available.'}
            </p>
            {isTruncated && (
              <button
                type="button"
                className="more-link"
                onClick={toggleExpand}
                aria-label={`Show ${isExpanded ? 'less' : 'more'} description for ${subject.chapter_name}`}
              >
                {isExpanded ? 'Less' : 'More'}
              </button>
            )}
          </div>
          <div className="icon-group">
            <span>
              <FontAwesomeIcon icon={faFilm} /> {videoCount}
            </span>
            <span>
              <FontAwesomeIcon icon={faFilePdf} /> {pdfCount}
            </span>
            <span>
              <FontAwesomeIcon icon={faImage} /> {imageCount}
            </span>
            <span>
              <FontAwesomeIcon icon={faPen} /> {testCount}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const getYouTubeEmbedUrl = (url) => {
    try {
      const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
      const match = url.match(regex);
      return match && match[1] ? `https://www.youtube.com/embed/${match[1]}` : null;
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      return null;
    }
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1];
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : 'https://via.placeholder.com/300x180?text=Thumbnail';
  };

  const TestDialog = ({ show, onHide, testGroup }) => {
    return (
      <Modal show={show} onHide={onHide} centered size="lg" className="test-dialog">
        <Modal.Header closeButton className="test-dialog-header">
          <Modal.Title className="test-dialog-title">
            <FontAwesomeIcon icon={faPen} className="me-2" />
            Test: {testGroup?.name || 'Untitled Test'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="test-dialog-body">
          {testGroup?.tests?.length > 0 ? (
            <div className="test-content">
              {testGroup.tests.map((test, index) => (
                <div key={test._id} className="test-question-card card mb-3 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <span className="question-number-badge badge bg-primary me-2">
                        {index + 1}
                      </span>
                      <h6 className="card-title mb-0">{test.question}</h6>
                    </div>
                    <ul className="test-options list-group">
                      {test.options.map((option, optIndex) => (
                        <li
                          key={optIndex}
                          className={`list-group-item test-option ${option === test.answer ? 'correct-answer' : ''
                            }`}
                          aria-label={`Option ${optIndex + 1}: ${option}${option === test.answer ? ' (correct answer)' : ''
                            }`}
                        >
                          <span className="option-number">{optIndex + 1}.</span> {option}
                          {option === test.answer && (
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="correct-icon ms-2"
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                    <p className="correct-answer-text mt-3 mb-0">
                      <strong>Correct Answer:</strong> {test.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data text-center">
              <FontAwesomeIcon icon={faPen} className="me-2" />
              No questions available in this test.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="test-dialog-footer">
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderContentTabs = useCallback((subject) => {
    const contentByType = {
      video: subject.content?.video || [],
      pdf: subject.content?.pdf || [],
      image: subject.content?.image || [],
      test: subject.content?.test || [],
    };

    return (
      <Tabs
        activeKey={selectedContentTab}
        onSelect={(k) => setSelectedContentTab(k)}
        className="content-tabs"
      >
        {[
          { key: 'video', label: 'Videos', icon: faFilm },
          { key: 'pdf', label: 'PDFs', icon: faFilePdf },
          { key: 'image', label: 'Images', icon: faImage },
          { key: 'test', label: 'Tests', icon: faPen },
        ].map(({ key, label, icon }) => (
          <Tab
            eventKey={key}
            title={
              <span className="tab-label">
                <FontAwesomeIcon icon={icon} className="tab-icon" />
                {label}
              </span>
            }
            key={key}
          >
            {contentByType[key].length > 0 ? (
              <div className="content-grid">
                {contentByType[key].map((item, index) => (
                  <div
                    className={`content-card content-${key}`}
                    key={index}
                    onClick={() => {
                      if (key === 'test') {
                        setSelectedTestGroup(item);
                        setShowTestDialog(true);
                      }
                    }}
                  >
                    <div className="card-content">
                      <h6 className="content-title">
                        {item.name || `Item ${index + 1}`}
                      </h6>
                      {key === 'video' && (
                        <div className="video-wrapper">
                          {!item.url ? (
                            <p className="error-text">Video URL is missing</p>
                          ) : getYouTubeEmbedUrl(item.url) ? (
                            <iframe
                              width="100%"
                              height="180"
                              src={getYouTubeEmbedUrl(item.url)}
                              title={videoTitles[item.url] || item.name || `Video ${index + 1}`}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="video-frame"
                              loading="lazy"
                            />
                          ) : (
                            <video controls width="100%" className="video-frame">
                              <source src={item.url} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          )}
                        </div>
                      )}
                      {key === 'pdf' && (
                        <div className="pdf-content">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="content-btn pdf-btn"
                            title={`Open ${item.name}`}
                            aria-label={`Open PDF ${item.name}`}
                          >
                            <FontAwesomeIcon icon={faFilePdf} />
                            View PDF
                          </a>
                          <span className="pdf-name">{item.name || 'Untitled PDF'}</span>
                        </div>
                      )}
                      {key === 'image' && (
                        <div className="image-wrapper">
                          <img
                            src={item.url || 'https://via.placeholder.com/300x200?text=No+Image'}
                            alt={item.name}
                            className="content-img"
                          />
                        </div>
                      )}
                      {key === 'test' && (
                        <div className="test-placeholder">
                          <span className="test-badge">Test</span>
                          <p>Click to view questions</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No {label.toLowerCase()} available.</p>
            )}
          </Tab>
        ))}
      </Tabs>
    );
  }, [selectedContentTab, videoTitles]);
  const filteredCourses = Array.isArray(coursesData)
    ? activeTab === 'all'
      ? coursesData
      : activeTab === 'free'
        ? coursesData.filter((course) => course && !course.is_paid)
        : coursesData.filter((course) => course && course.is_paid)
    : [];

  return (
    <div className="main-all-courses">
      <img
        src={judicialLogo}
        alt="Judicial Logo"
        className="judicial-logo"
        onClick={() => {
          setCurrentScreen('courses');
          setSelectedCourseCard(null);
          setSelectedLesson(null);
          setSelectedSubject(null);
          setActiveTab('all');
        }}
      />
      <div className="container">
        <button
          type="button"
          className="create-course-btn"
          onClick={() => dispatch(openCreateNewCourseDialog({ isOpen: true, mode: 'create' }))}
        >
          <FontAwesomeIcon icon={faPlus} /> Create New Course
        </button>

        <Modal show={showDeleteCourseDialog} centered>
          <Modal.Body className="text-center">
            <h5 className="mt-3">Are you sure you want to delete this Course?</h5>
            <hr />
            <p className="my-5">This action will permanently remove it from the database.</p>
            <div className="d-flex justify-content-center mb-3">
              <Button
                variant="secondary"
                className="me-3"
                onClick={() => {
                  setShowDeleteCourseDialog(false);
                  setSelectedCourseId(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleCourseDelete}>
                Delete
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        <TestDialog
          show={showTestDialog}
          onHide={() => {
            setShowTestDialog(false);
            setSelectedTestGroup(null);
          }}
          testGroup={selectedTestGroup}
        />

        {currentScreen === 'courses' && (
          <>
            <h3 className="section-title">All Courses</h3>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="course-filter-tabs"
            >
              <Tab eventKey="all" title="All">
                {filteredCourses.length === 0 ? (
                  <p className="no-data">No courses found.</p>
                ) : (
                  <div className="card-grid">
                    {filteredCourses.map((course) => (
                      <CourseCard key={course._id} course={course} />
                    ))}
                  </div>
                )}
              </Tab>
              <Tab eventKey="free" title="Free" className="mx-5">
                {filteredCourses.length === 0 ? (
                  <p className="no-data">No free courses found.</p>
                ) : (
                  <div className="card-grid">
                    {filteredCourses.map((course) => (
                      <CourseCard key={course._id} course={course} />
                    ))}
                  </div>
                )}
              </Tab>
              <Tab eventKey="paid" title="Paid">
                {filteredCourses.length === 0 ? (
                  <p className="no-data">No paid courses found.</p>
                ) : (
                  <div className="card-grid">
                    {filteredCourses.map((course) => (
                      <CourseCard key={course._id} course={course} />
                    ))}
                  </div>
                )}
              </Tab>
            </Tabs>
          </>
        )}

        {currentScreen === 'lessons' && selectedCourseCard && (
          <>
            <BackButton
              title="Back to Courses"
              onClick={() => {
                setCurrentScreen('courses');
                setSelectedCourseCard(null);
                setSelectedLesson(null);
                setSelectedSubject(null);
              }}
            />
            <h3 className="section-title">{selectedCourseCard.title || 'Untitled Course'} - Lessons</h3>
            {selectedCourseCard.lessons?.length === 0 ? (
              <p className="no-data">No lessons found.</p>
            ) : (
              <div className="card-grid">
                {selectedCourseCard.lessons.map((lesson, index) => (
                  <LessonCard key={index} lesson={lesson} />
                ))}
              </div>
            )}
          </>
        )}

        {currentScreen === 'subjects' && selectedLesson && (
          <>
            <BackButton
              title="Back to Lessons"
              onClick={() => {
                setCurrentScreen('lessons');
                setSelectedLesson(null);
                setSelectedSubject(null);
              }}
            />
            <h3 className="section-title">{selectedLesson.lesson_name || 'Untitled Lesson'} - Subjects</h3>
            {selectedLesson.subjects?.length === 0 ? (
              <p className="no-data">No subjects found.</p>
            ) : (
              <div className="card-grid">
                {selectedLesson.subjects.map((subject, index) => (
                  <SubjectCard key={index} subject={subject} />
                ))}
              </div>
            )}
          </>
        )}

        {currentScreen === 'subjectDetails' && selectedSubject && (
          <>
            <BackButton
              title="Back to Subjects"
              onClick={() => {
                setCurrentScreen('subjects');
                setSelectedSubject(null);
              }}
            />
            <h3 className="section-title">{selectedSubject.chapter_name || 'Untitled Subject'} - Content</h3>
            {renderContentTabs(selectedSubject)}
          </>
        )}
      </div>
    </div>
  );
};

export default AllCoursesShowing;